import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Star, MapPin, Clock, MessageCircle, Instagram } from "lucide-react";
import SEO from "@/components/SEO";
import Marquee from "@/components/Marquee";
import { Reveal, Eyebrow, EASE } from "@/components/Reveal";
import { useBooking } from "@/components/booking";
import { BUSINESS, SERVICES, REVIEWS, IMAGES, WA_DEFAULT } from "@/data/business";

function HeroLine({ children, delay, reduce }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={reduce ? false : { y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.95, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Hero() {
  const { openBooking } = useBooking();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const ySmall = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative overflow-hidden bg-ivory">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full border border-rose/15" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl gap-12 px-5 pb-20 pt-28 sm:px-8 sm:pt-32 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-8 lg:pt-40 lg:pb-28">
        <div>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            className="flex items-center gap-3"
          >
            <span className="h-px w-10 bg-rose" aria-hidden="true" />
            <Eyebrow>Glam Girl Makeup Studio — Varanasi</Eyebrow>
          </motion.div>

          <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.06] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
            <HeroLine delay={0.25} reduce={reduce}>Beauty, styled</HeroLine>
            <HeroLine delay={0.37} reduce={reduce}>
              with <em className="text-rose">confidence.</em>
            </HeroLine>
          </h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            className="mt-6 max-w-md text-base leading-relaxed text-charcoal/65"
          >
            A ladies salon and hair &amp; makeup studio in the Patel Nagar / Hardattpur
            area of Varanasi — refined beauty, close to home.
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.72, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => openBooking()}
              data-testid="hero-book-button"
              className="group inline-flex items-center gap-2 rounded-full bg-charcoal px-8 py-4 text-sm font-semibold tracking-wide text-ivory transition-colors hover:bg-rose-deep"
            >
              Book Appointment
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 rounded-full border border-charcoal/20 px-7 py-4 text-sm font-semibold tracking-wide text-charcoal transition-colors hover:border-rose hover:text-rose"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp Now
            </a>
            <Link
              to="/services"
              data-testid="hero-explore-link"
              className="group inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal/70 transition-colors hover:text-rose"
            >
              Explore Services
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </motion.div>

          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex items-center gap-2.5"
            data-testid="hero-rating"
          >
            <span className="flex" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-rose text-rose" />
              ))}
            </span>
            <span className="text-sm font-semibold text-charcoal">{BUSINESS.rating}</span>
            <span className="text-sm text-charcoal/55">· {BUSINESS.reviewCount} Google reviews</span>
          </motion.div>
        </div>

        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
            className="relative"
          >
            <motion.div style={{ y: yMain }} className="relative">
              <div className="arch overflow-hidden border border-rose/20 shadow-[0_30px_80px_rgba(27,25,22,0.18)]">
                <img
                  src={IMAGES.bridalPortrait.src}
                  alt={IMAGES.bridalPortrait.alt}
                  className="aspect-[3/4] w-full object-cover"
                  fetchpriority="high"
                />
              </div>
              <div className="pointer-events-none absolute inset-0 arch border border-white/30" aria-hidden="true" />
            </motion.div>
            <motion.div
              style={{ y: ySmall }}
              className="absolute -bottom-10 left-2 w-36 rotate-[-5deg] overflow-hidden rounded-xl border-4 border-ivory shadow-[0_20px_50px_rgba(27,25,22,0.22)] sm:-left-12 sm:w-52"
            >
              <img
                src={IMAGES.salonInterior.src}
                alt={IMAGES.salonInterior.alt}
                className="aspect-[4/3] w-full object-cover"
                loading="lazy"
              />
            </motion.div>
            <div className="animate-float-slow absolute right-2 top-8 rounded-full border border-charcoal/10 bg-ivory/95 px-4 py-2 shadow-lg backdrop-blur sm:-right-6" data-testid="hero-open-badge">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-charcoal/70">
                Open Daily · 10 AM – 8 PM
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  const items = [
    { icon: Star, title: `${BUSINESS.rating} ★`, sub: `${BUSINESS.reviewCount} Google reviews`, testid: "trust-rating" },
    { icon: Clock, title: "Open Daily", sub: BUSINESS.hoursShort, testid: "trust-hours" },
    { icon: MapPin, title: "Visit Us", sub: "Patel Nagar / Hardattpur, Varanasi", testid: "trust-location" },
  ];
  return (
    <section data-testid="trust-strip" className="border-y border-charcoal/10 bg-cream">
      <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-charcoal/10 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {items.map((it) => (
          <div key={it.testid} data-testid={it.testid} className="flex items-center justify-center gap-4 px-6 py-6">
            <it.icon className="h-5 w-5 text-rose" />
            <div>
              <p className="text-sm font-bold tracking-wide text-charcoal">{it.title}</p>
              <p className="text-xs text-charcoal/55">{it.sub}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ServicesPreview() {
  const { openBooking } = useBooking();
  const withImage = { hair: IMAGES.salonInterior, "bridal-occasion": IMAGES.bridalMirror };
  return (
    <section data-testid="services-preview" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <Reveal>
          <Eyebrow>What We Do</Eyebrow>
          <h2 className="mt-4 max-w-lg font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            Services designed around your look.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link
            to="/services"
            data-testid="services-preview-all-link"
            className="group inline-flex items-center gap-1.5 text-sm font-semibold text-charcoal/70 transition-colors hover:text-rose"
          >
            View all services
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((cat, i) => (
          <Reveal key={cat.id} delay={0.05 * (i % 3)}>
            <Link
              to={`/services#${cat.id}`}
              data-testid={`service-card-${cat.id}`}
              className="group flex h-full flex-col rounded-2xl border border-charcoal/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-rose/35 hover:shadow-[0_24px_50px_rgba(27,25,22,0.1)]"
            >
              {withImage[cat.id] && (
                <div className="mb-6 overflow-hidden rounded-xl">
                  <img
                    src={withImage[cat.id].src}
                    alt={withImage[cat.id].alt}
                    loading="lazy"
                    className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              )}
              <div className="flex items-baseline justify-between">
                <span className="font-serif text-sm italic text-rose">{String(i + 1).padStart(2, "0")}</span>
                <ArrowUpRight className="h-4 w-4 text-charcoal/30 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-rose" />
              </div>
              <h3 className="mt-3 font-serif text-2xl font-medium text-charcoal">{cat.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-charcoal/60">{cat.tagline}</p>
              <ul className="mt-5 space-y-2 border-t border-charcoal/8 pt-5">
                {cat.items.slice(0, 4).map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-[13px] text-charcoal/65">
                    <span className="h-1 w-1 shrink-0 rounded-full bg-rose/60" aria-hidden="true" />
                    {item}
                  </li>
                ))}
                {cat.items.length > 4 && (
                  <li className="pl-3.5 text-[12px] italic text-charcoal/45">
                    + {cat.items.length - 4} more
                  </li>
                )}
              </ul>
              <span className="mt-auto pt-6 text-xs font-semibold uppercase tracking-[0.18em] text-charcoal/45 transition-colors group-hover:text-rose">
                Explore Services
              </span>
            </Link>
          </Reveal>
        ))}
        <Reveal delay={0.1}>
          <button
            onClick={() => openBooking()}
            data-testid="services-preview-book-card"
            className="group flex h-full min-h-[220px] w-full flex-col justify-between rounded-2xl bg-charcoal p-7 text-left transition-colors duration-300 hover:bg-rose-deep"
          >
            <p className="font-serif text-2xl font-medium leading-snug text-ivory">
              Not sure what you need? <em className="text-champagne">Just ask us.</em>
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ivory">
              Book Appointment
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </button>
        </Reveal>
      </div>
    </section>
  );
}

function BridalSpotlight() {
  const { openBooking } = useBooking();
  return (
    <section data-testid="bridal-spotlight" className="grain relative overflow-hidden bg-ink text-ivory">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:py-32">
        <Reveal className="relative order-2 lg:order-1">
          <div className="arch mx-auto max-w-sm overflow-hidden border border-champagne/25">
            <img
              src={IMAGES.bridalDoor.src}
              alt={IMAGES.bridalDoor.alt}
              loading="lazy"
              className="aspect-[3/4] w-full object-cover"
            />
          </div>
          <div className="absolute -bottom-4 left-1/2 h-24 w-[85%] -translate-x-1/2 rounded-[50%] bg-black/50 blur-2xl" aria-hidden="true" />
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal>
            <Eyebrow dark>Bridal &amp; Occasion</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight sm:text-4xl lg:text-5xl">
              Your occasion, <em className="text-champagne">your look.</em>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ivory/60">
              Bridal hair, bridal services and make-up — done calmly, carefully and
              hygienically, right here at the studio in Patel Nagar.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-9 flex flex-wrap gap-4">
              <button
                onClick={() => openBooking("Bridal Services")}
                data-testid="bridal-spotlight-book-button"
                className="rounded-full bg-ivory px-8 py-4 text-sm font-semibold tracking-wide text-ink transition-colors hover:bg-champagne"
              >
                Book Bridal Consultation
              </button>
              <Link
                to="/bridal-makeup"
                data-testid="bridal-spotlight-explore-link"
                className="group inline-flex items-center gap-1.5 rounded-full border border-ivory/25 px-7 py-4 text-sm font-semibold tracking-wide text-ivory transition-colors hover:border-champagne hover:text-champagne"
              >
                Explore Bridal
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  return (
    <section data-testid="reviews-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
      <Reveal className="max-w-xl">
        <Eyebrow>Client Words</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
          {BUSINESS.rating} on Google — <em className="text-rose">in their words.</em>
        </h2>
        <p className="mt-5 text-base leading-relaxed text-charcoal/60">
          Verified reviews from our Google profile · {BUSINESS.reviewCount} reviews in total.
        </p>
      </Reveal>
      <div className="no-scrollbar mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4" data-testid="reviews-row">
        {REVIEWS.map((r, i) => (
          <Reveal key={i} delay={0.04 * (i % 4)} className="shrink-0 snap-start">
            <figure className="flex h-full w-[300px] flex-col rounded-2xl border border-charcoal/10 bg-white p-7 sm:w-[340px]">
              <div className="flex gap-0.5" aria-label="5 star review">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} className="h-3.5 w-3.5 fill-rose text-rose" />
                ))}
              </div>
              <blockquote className="mt-4 flex-1 font-serif text-lg italic leading-relaxed text-charcoal/85">
                “{r.text}”
              </blockquote>
              <figcaption className="mt-5 border-t border-charcoal/10 pt-4 text-xs font-semibold uppercase tracking-[0.16em] text-charcoal/55">
                {r.name} · Google review
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function InstagramSection() {
  const tiles = [IMAGES.bridalPortrait, IMAGES.bridalMirror, IMAGES.salonInterior, IMAGES.bridalDoor];
  return (
    <section data-testid="instagram-section" className="border-y border-charcoal/10 bg-cream">
      <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <Eyebrow>Instagram</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
              Follow the latest looks.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-button"
              className="group inline-flex items-center gap-2 rounded-full border border-charcoal/20 px-7 py-3.5 text-sm font-semibold text-charcoal transition-colors hover:border-rose hover:text-rose"
            >
              <Instagram className="h-4 w-4" /> Follow on Instagram
            </a>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {tiles.map((img, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <a
                href={BUSINESS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`instagram-tile-${i}`}
                className="group block overflow-hidden rounded-xl"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </a>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-charcoal/45">{BUSINESS.instagramHandle}</p>
      </div>
    </section>
  );
}

function VisitTeaser() {
  const { openBooking } = useBooking();
  return (
    <section data-testid="visit-teaser" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <Eyebrow>Find Us</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-charcoal sm:text-4xl lg:text-5xl">
            Visit Glam Girl Makeup Studio
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-charcoal/65">{BUSINESS.address}</p>
          <p className="mt-3 text-sm font-semibold text-charcoal">{BUSINESS.hoursLine}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={BUSINESS.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="visit-teaser-directions-button"
              className="group inline-flex items-center gap-2 rounded-full bg-charcoal px-7 py-3.5 text-sm font-semibold text-ivory transition-colors hover:bg-rose-deep"
            >
              <MapPin className="h-4 w-4" /> Get Directions
            </a>
            <button
              onClick={() => openBooking()}
              data-testid="visit-teaser-book-button"
              className="rounded-full border border-charcoal/20 px-7 py-3.5 text-sm font-semibold text-charcoal transition-colors hover:border-rose hover:text-rose"
            >
              Book Appointment
            </button>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="overflow-hidden rounded-2xl border border-charcoal/10 shadow-[0_24px_60px_rgba(27,25,22,0.12)]">
            <iframe
              title="Glam Girl Makeup Studio on Google Maps"
              src={BUSINESS.mapsEmbed}
              className="aspect-[4/3] w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="home-map-embed"
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      <SEO
        title=""
        description="Glam Girl Makeup Studio — a ladies salon and hair & makeup studio in Patel Nagar / Hardattpur, Varanasi. Hair, colour, keratin, braiding, waxing, makeup & bridal services. Rated 4.8 on Google. Open daily 10 AM – 8 PM."
      />
      <Hero />
      <Marquee dark />
      <TrustStrip />
      <ServicesPreview />
      <BridalSpotlight />
      <Reviews />
      <InstagramSection />
      <VisitTeaser />
    </main>
  );
}
